"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Icon } from "@iconify/react";
import { cn } from "../../lib/utils";
import gsap from "gsap";
import { useEffect, useState, useRef } from "react";
import { useTheme } from "next-themes";
import { MobileMenu } from "./mobile-menu";
import { LanguageSwitcher } from "@/app/components/ui/language-switcher";
import { useTranslation } from 'react-i18next';

import { NAV_ITEMS, MOBILE_NAV_GROUPS } from "./nav-data";
import { SidebarProps } from "./types";



/**
 * Top navbar navigation component
 * Fixed to the top of the viewport on all screen sizes.
 * Desktop: shows all nav links with icon + label.
 * Mobile: shows theme toggle + hamburger that opens the drawer.
 */
export function Sidebar({ onClose, isMobile = false, onMobileMenuOpen }: SidebarProps) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  const { theme, setTheme } = useTheme();

  const headerRef = useRef<HTMLDivElement>(null);
  const navItemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const controlsRef = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    setMounted(true);

    // Initial GSAP animation replacing framer-motion variants
    const tl = gsap.timeline();

    if (headerRef.current) {
      tl.fromTo(headerRef.current,
        { y: -60, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: "back.out(1.5)" }
      );
    }

    const validNavItems = navItemsRef.current.filter(Boolean);
    const validControls = controlsRef.current.filter(Boolean);

    if (validNavItems.length > 0 || validControls.length > 0) {
      tl.fromTo([...validNavItems, ...validControls],
        { y: -8, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.4, stagger: 0.04, ease: "power2.out" },
        "-=0.4"
      );
    }
  }, []);

  // Lock body scroll while mobile drawer is open
  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "auto";
      };
    }
  }, [isMobile]);

  // Mobile drawer — delegate to MobileMenu
  if (isMobile) {
    return (
      <MobileMenu
        onClose={onClose || (() => { })}
        navItems={MOBILE_NAV_GROUPS}
        theme={theme}
        setTheme={setTheme}
        mounted={mounted}
      />
    );
  }

  // Desktop / always-visible top navbar
  return (
    <div className="fixed top-6 left-0 right-0 px-6 flex justify-end lg:justify-center z-50 pointer-events-none">
      <header
        ref={headerRef}
        className="pointer-events-auto flex items-center gap-2 p-1.5 rounded-full bg-white/95 dark:bg-[#0a0a0a]/90 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.1)] transition-all duration-300"
      >
        {/* Nav links — hidden on mobile, visible on desktop */}
        <nav className="hidden lg:flex items-center">
          {NAV_ITEMS.map((item) => {
            const isActive = mounted && (
              pathname === item.href ||
              (pathname && item.href !== "/" && pathname.startsWith(item.href))
            );
            return (
              <div
                key={item.href}
                ref={el => { navItemsRef.current[NAV_ITEMS.indexOf(item)] = el }}
                className="relative"
              >
                <Link
                  href={item.href}
                  prefetch={false}
                  className={cn(
                    "relative flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300",
                    isActive
                      ? "text-slate-900 dark:text-white"
                      : "text-slate-700 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white"
                  )}
                >
                  {isActive && mounted && (
                    <span className="absolute inset-0 bg-slate-100/80 dark:bg-white/10 rounded-full z-0 transition-all duration-300" />
                  )}
                  <span className="relative z-10 flex items-center gap-2">
                    <span>{t(`Navbar.${item.name}`)}</span>
                  </span>
                </Link>
              </div>
            );
          })}
        </nav>

        {/* Separator for desktop */}
        <div className="hidden lg:block w-px h-6 bg-slate-200 dark:bg-white/10 mx-1" />

        {/* Controls */}
        <div className="flex items-center gap-1.5 px-1">
          <button
            ref={el => { controlsRef.current[0] = el }}
            data-cal-namespace="30min"
            data-cal-link="ellis-caicedo-hfdo7f/30min"
            data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}'
            className="hidden sm:flex items-center gap-2 px-4 py-2 bg-[#181A1D] dark:bg-white text-white dark:text-[#181A1D] rounded-full text-xs font-bold hover:scale-105 hover:-translate-y-px active:scale-95 transition-all duration-300 shadow-lg shadow-black/10 dark:shadow-white/5 mr-1"
          >
            <span>{t('Navbar.scheduleCall')}</span>
            <Icon icon="solar:arrow-right-up-linear" width="16" height="16" />
          </button>

          <LanguageSwitcher />

          {/* Theme toggle */}
          <button
            ref={el => { controlsRef.current[2] = el }}
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="w-9 h-9 flex items-center justify-center rounded-full text-slate-700 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 hover:scale-110 active:scale-95 transition-all duration-300"
            aria-label="Toggle theme"
          >
            {mounted ? (
              theme === "dark" ? (
                <Icon icon="solar:sun-2-bold-duotone" width="22" height="22" className="text-amber-400" />
              ) : (
                <Icon icon="solar:moon-bold-duotone" width="22" height="22" className="text-slate-800" />
              )
            ) : (
              <div className="w-[22px] h-[22px]" />
            )}
          </button>

          {/* Hamburger — mobile/tablet only */}
          <button
            ref={el => { controlsRef.current[3] = el }}
            onClick={onMobileMenuOpen}
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full text-slate-700 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 hover:scale-105 active:scale-95 transition-all duration-300"
            aria-label="Open menu"
          >
            <Icon icon="solar:hamburger-menu-linear" width="22" height="22" />
          </button>
        </div>
      </header>
    </div>
  );
}
